// ImageGallery.js
import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';

Modal.setAppElement('#root');

const ImageGallery = ({ selectedNoteId, refreshTrigger }) => {
  console.log("ImageGallery received noteId:", selectedNoteId, "refreshTrigger:", refreshTrigger);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchImages = useCallback(async () => {
    try {
      console.log("Making fetch request for noteId:", selectedNoteId);
      const response = await fetch(`http://172.234.197.110:8000/api/notes/${selectedNoteId}/images`, {
        credentials: 'include'  // Sends HTTP-only cookies automatically
        });

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }
      const data = await response.json();
      console.log("Fetched images:", data.length);
      setImages(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [selectedNoteId]);

  useEffect(() => {
    console.log("ImageGallery: refreshTrigger changed to:", refreshTrigger);
    if (selectedNoteId) {
      fetchImages();
    }
  }, [selectedNoteId, refreshTrigger, fetchImages]);

  const handleThumbnailClick = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  const openImageInNewTab = () => {
    if (selectedImage) {
      window.open(
        `http://172.234.197.110:8000/uploaded_images/${selectedImage.filename}`,
        '_blank',
        'width=800,height=600,toolbar=no,scrollbars=yes,resizable=yes'
      );
      closeModal();
    }
  };

  const handleUpdateDescription = async (imageId, description) => {
    try {
      const response = await fetch(
        `http://172.234.197.110:8000/api/notes/${selectedNoteId}/images/${imageId}`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            credentials: 'include',
            body: `description=${encodeURIComponent(description)}`,
        }
    );

      if (!response.ok) {
        throw new Error('Failed to update description');
      }

      // Update the image in state
      setImages(prevImages => 
        prevImages.map(img => 
          img.id === imageId 
            ? { ...img, description } 
            : img
        )
      );
      
      // Update selected image if it's the one being modified
      if (selectedImage && selectedImage.id === imageId) {
        setSelectedImage(prev => ({ ...prev, description }));
      }
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      const response = await fetch(
        `http://172.234.197.110:8000/api/notes/${selectedNoteId}/images/${imageId}`,
        { 
            method: 'DELETE',
            credentials: 'include'  // Ensures cookies are sent with the request
        }
      );
      
      if (!response.ok) throw new Error('Failed to delete image');
      
      // Remove image from state
      setImages(prevImages => prevImages.filter(img => img.id !== imageId));
      
      // Close modal if the deleted image was being viewed
      if (selectedImage && selectedImage.id === imageId) {
        closeModal();
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const modalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      padding: '20px',
      backgroundColor: '#2c2c2c',
      border: '1px solid #444',
      maxWidth: '90%',
      maxHeight: '90%',
      zIndex: 9999
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 9998
    }
  };

  if (!selectedNoteId) {
    return <div className="h-full bg-gray-800 text-white p-4">Please select a note to view images.</div>;
  }

  if (loading) {
    return <div className="h-full bg-gray-800 text-white p-4">Loading images...</div>;
  }

  if (error) {
    return <div className="h-full bg-gray-800 text-white p-4">Error: {error}</div>;
  }

  return (
    <div className="h-full bg-gray-800 text-white p-4 overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Image Gallery</h2>
        <span className="text-sm text-gray-400">{images.length} images</span>
      </div>
      
      {images.length === 0 ? (
        <div className="text-center text-gray-400 py-8">
          No images found. Paste or upload images in the note editor.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {images.map((image) => (
            <div 
              key={image.id} 
              className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-700"
            >
              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteImage(image.id);
                }}
                className="absolute top-2 right-2 z-10 p-1 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Image (clicking this opens modal) */}
              <div onClick={() => handleThumbnailClick(image)}>
                <img
                  src={`http://172.234.197.110:8000/uploaded_images/${image.filename}`}
                  alt={image.description || 'Note Image'}
                  className="w-full h-32 object-cover transition-transform duration-200 group-hover:scale-105"
                />
                {image.description && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm truncate">
                    {image.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={modalStyles}
        contentLabel="Image Gallery Modal"
      >
        {selectedImage && (
          <div className="text-white">
            <img
              src={`http://172.234.197.110:8000/uploaded_images/${selectedImage.filename}`}
              alt={selectedImage.description || 'Note Image'}
              className="max-w-full max-h-[70vh] object-contain mb-4"
            />
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={selectedImage.description || ''}
                onChange={(e) => handleUpdateDescription(selectedImage.id, e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="Add description..."
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={openImageInNewTab}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded"
                >
                  Open in New Tab
                </button>
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ImageGallery;